
import React from 'react';
import { Mail, Paperclip, Code } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';

interface EmailTemplateTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

export const EmailTemplateTab: React.FC<EmailTemplateTabProps> = ({ data, onChange }) => {
    return (
        <div className="p-6 space-y-6">
            <div className="space-y-4">
                 <div className="grid grid-cols-2 gap-3">
                    <PropertyField 
                        config={{ name: 'fromEmail', label: 'From', type: 'email', placeholder: 'sender@example.com', validation: { required: true, email: true } }}
                        value={data.fromEmail}
                        onChange={onChange}
                    />
                     <PropertyField 
                        config={{ name: 'fromName', label: 'Sender Name', type: 'text', placeholder: 'System' }}
                        value={data.fromName}
                        onChange={onChange}
                    />
                 </div>

                 <PropertyField 
                    config={{ name: 'to', label: 'To', type: 'text', placeholder: 'recipient@example.com, ${user.email}', description: 'Supports variables' }}
                    value={data.to}
                    onChange={onChange}
                />
                
                <div className="grid grid-cols-2 gap-3">
                    <PropertyField 
                        config={{ name: 'cc', label: 'CC', type: 'text', placeholder: 'cc@example.com' }}
                        value={data.cc}
                        onChange={onChange}
                    />
                    <PropertyField 
                        config={{ name: 'bcc', label: 'BCC', type: 'text', placeholder: 'bcc@example.com' }}
                        value={data.bcc}
                        onChange={onChange}
                    />
                </div>
            </div>

            <hr className="border-slate-200 dark:border-slate-800" />

            <div className="space-y-4">
                 <PropertyField 
                    config={{ name: 'subject', label: 'Subject', type: 'text', placeholder: 'Subject line...', validation: { required: true } }}
                    value={data.subject}
                    onChange={onChange}
                />
                
                <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Content</label>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 uppercase font-bold">Format:</span>
                        <button 
                            className={`p-1 rounded ${data.isHtml ? 'bg-teal-100 text-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
                            onClick={() => onChange('isHtml', !data.isHtml)}
                            title="Toggle HTML Mode"
                        >
                            <Code size={14} />
                        </button>
                    </div>
                </div>
                
                <textarea 
                    value={data.body || ''}
                    onChange={(e) => onChange('body', e.target.value)}
                    rows={8}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 ${data.isHtml ? 'font-mono bg-slate-50 border-slate-300' : 'bg-white border-slate-300'}`}
                    placeholder={data.isHtml ? '<html><body>...</body></html>' : 'Email body content...'}
                />
            </div>

            <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <Paperclip size={12} /> Attachments
                </label>
                <PropertyField 
                    config={{ 
                        name: 'attachments', 
                        label: '', // Hidden label
                        type: 'array', 
                        itemType: 'text',
                        placeholder: 'File URL or path'
                    }}
                    value={data.attachments}
                    onChange={onChange}
                />
            </div>
        </div>
    );
};
